import {
  Box,
  Card,
  Divider,
  Flex,
  MultiSelect,
  Slider,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';
import Icon from '../../../components/Icon';
import Loading from '../../../components/Loading';
import { ManifestCheckbox } from '../../../components/ManifestCheckbox/ManifestCheckbox';
import { useNotifications } from '../../../hooks/utils/useNotifications';
import { useAutosave } from './useAutosave.hook';
import {
  useVulnerabilityAlertConfig,
  VulnerabilityAlertConfiguration,
} from './useVulnerabilityAlertConfig.hook';
import styles from './VulnAlerts.module.scss';
import axios from 'axios';

/* ============================================================================
 * Types & Constants
 * ========================================================================== */
const RECOMMENDATION_KEYS = ['mitigate', 'monitor', 'accept'] as const;

/* ============================================================================
 * Component
 * ========================================================================== */
export const VulnAlertsTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { fetchConfig, saveConfig } = useVulnerabilityAlertConfig();
  const { error: notifyError } = useNotifications();

  /* ------------------------------------------------------------------------
   * Local UI State (kept outside the form on purpose)
   * ---------------------------------------------------------------------- */
  const [recEnabled, setRecEnabled] = useState(true);
  const [severityEnabled, setSeverityEnabled] = useState(false);
  const [emailOptions, setEmailOptions] = useState<string[]>([]);
  const [inited, setInited] = useState(false);
  const [prevSeverity, setPrevSeverity] = useState<number>(8);
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [criteriaError, setCriteriaError] = useState(false);
  const isInitialMount = useRef(true);

  /* ------------------------------------------------------------------------
   * Form - main data model sent to the API
   * ---------------------------------------------------------------------- */
  const form = useForm<VulnerabilityAlertConfiguration>({
    initialValues: {
      isActive: false,
      alertOnKev: true,
      alertOnRecommendation: ['mitigate'],
      alertOnSeverity: 0,
      targetEmails: [],
    },
    validate: {
      targetEmails: isNotEmpty(
        <Flex direction="row" align="center" gap={4}>
          <Icon classNames={styles.error} icon="triangle-exclamation" />
          <span>{t('page.userSettings.vulnAlerts.validation.addAtLeastOneEmail')}</span>
        </Flex>,
      ),
    },
  });

  /* ------------------------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------------------- */
  const canUpdateVulnAlertSettings = useFetchUserHasPermissions({
    requiredPermissions: ['create:vulnerability-alert-configuration'],
  });

  const showGenericError = useCallback(
    () =>
      notifyError({
        title: t('page.userSettings.vulnAlerts.validation.requestError'),
        autoClose: 5000,
      }),
    [notifyError, t],
  );

  const createSavePayload = useCallback(
    () => ({
      ...form.values,
      // Only include severity threshold if enabled; otherwise send 0
      alertOnSeverity: severityEnabled ? form.values.alertOnSeverity : 0,
    }),
    [form.values, severityEnabled],
  );

  /** Adds a new email to both the selectable list and the current selection. */
  const handleCreateEmail = (query: string) => {
    const email = query.trim();
    if (!email) return email;

    if (!emailOptions.includes(email)) setEmailOptions((prev) => [...prev, email]);
    if (!form.values.targetEmails.includes(email)) {
      form.setFieldValue('targetEmails', [...form.values.targetEmails, email]);
    }
    return email;
  };

  /** Human‑readable label summarising selected recommendations. */
  const recommendationLabel = useMemo(() => {
    const recs = form.values.alertOnRecommendation;
    if (recs.length === 0) {
      return t('page.userSettings.vulnAlerts.recommendation.title', {
        recommendation: '',
      });
    }
    const labels = recs.map((key) =>
      t(`page.userSettings.vulnAlerts.recommendation.${key}`),
    );
    return t('page.userSettings.vulnAlerts.recommendation.title', {
      recommendation: labels.join(', '),
    }).trimEnd();
  }, [form.values.alertOnRecommendation, t, i18n.language]);

  const validateFormForAutosave = useCallback(() => {
    const { hasErrors } = form.validate(); // Triggers validation (e.g., email required)

    // If recommendations toggle is on, at least one recommendation must be selected
    if (recEnabled && form.values.alertOnRecommendation.length === 0) {
      return false;
    }

    // At least one criterion (Kev, Recommendation, or Severity) must be selected if alerts are active
    const noCriteria =
      !form.values.alertOnKev &&
      !(recEnabled && form.values.alertOnRecommendation.length > 0) &&
      !severityEnabled;

    return !hasErrors && !noCriteria;
  }, [form, recEnabled, severityEnabled]);

  const handleAutosave = useCallback(
    (payload: VulnerabilityAlertConfiguration) => {
      if (!validateFormForAutosave()) return;
      // use the exact data the hook thought was new,
      // instead of re-reading form.values from an old closure:
      saveConfig.mutate(payload);
    },
    [validateFormForAutosave, saveConfig],
  );

  const handleAutosaveSuccess = useCallback(() => {
    isInitialMount.current = true;
  }, []);

  const handleAutosaveError = useCallback(() => {
    // Error notification is handled by the autosave hook
  }, []);

  /* ------------------------------------------------------------------------
   * Set up autosave
   * ---------------------------------------------------------------------- */
  const {
    clearAutosaveTimeout,
    handleSaveSuccess,
    handleSaveError,
    markAsUserInteraction,
    updateLastSavedData,
  } = useAutosave({
    data: createSavePayload(), // Use the processed payload instead of raw form.values
    onSave: handleAutosave,
    delay: 0,
    isLoading: saveConfig.isLoading,
    enabled: inited && form.values.isActive && canUpdateVulnAlertSettings,
    onSuccess: handleAutosaveSuccess,
    onError: handleAutosaveError,
    validate: validateFormForAutosave, // Ensure autosave only runs when form is valid
  });

  /* ------------------------------------------------------------------------
   * Handle save mutation results
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (saveConfig.isSuccess) {
      const response = saveConfig.data;
      if (response?.success) {
        // Update the last saved data reference with the actual payload that was sent
        const savedPayload = createSavePayload();
        updateLastSavedData(savedPayload);
        handleSaveSuccess();
        // Reset mutation state to avoid stale success state on next autosave
        saveConfig.reset();
      } else {
        handleSaveError();
      }
    }
  }, [
    saveConfig.isSuccess,
    saveConfig.data,
    handleSaveSuccess,
    handleSaveError,
    updateLastSavedData,
    createSavePayload,
  ]);

  useEffect(() => {
    if (saveConfig.isError) {
      handleSaveError();
      // Reset mutation state to avoid stale error state on next autosave
      saveConfig.reset();
    }
  }, [saveConfig.isError, handleSaveError]);

  /* ------------------------------------------------------------------------
   * Criteria selection validation effect
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (form.values.isActive) {
      if (recEnabled && form.values.alertOnRecommendation.length === 0) {
        setCriteriaError(true);
      } else {
        const noCriteria =
          !form.values.alertOnKev &&
          !(recEnabled && form.values.alertOnRecommendation.length > 0) &&
          !severityEnabled;
        setCriteriaError(noCriteria);
      }
    } else {
      // If alerts are not active, no criteria needed
      setCriteriaError(false);
    }
  }, [
    form.values.isActive,
    recEnabled,
    form.values.alertOnRecommendation,
    form.values.alertOnKev,
    severityEnabled,
  ]);

  /* ------------------------------------------------------------------------
   * Handle fetch errors
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (fetchConfig.isError) showGenericError();
  }, [fetchConfig.isError, showGenericError]);

  /* ------------------------------------------------------------------------
   * Hydrate initial config
   * ---------------------------------------------------------------------- */
  useEffect(() => {
    if (
      fetchConfig.isSuccess &&
      !inited &&
      fetchConfig.data?.data &&
      fetchConfig.data.success
    ) {
      const d = fetchConfig.data.data;
      const initialSeverity = d.alertOnSeverity > 0 ? d.alertOnSeverity : 8;

      const formData = {
        isActive: d.isActive,
        alertOnKev: d.alertOnKev,
        alertOnRecommendation: Array.isArray(d.alertOnRecommendation)
          ? d.alertOnRecommendation
          : [],
        alertOnSeverity: initialSeverity,
        targetEmails: d.targetEmails,
      };

      form.setValues(formData);

      // Set the UI state based on fetched data
      setPrevSeverity(initialSeverity);
      setRecEnabled(
        Array.isArray(d.alertOnRecommendation) && d.alertOnRecommendation.length > 0,
      );
      setSeverityEnabled(d.alertOnSeverity > 0);
      setEmailOptions(d.targetEmails);
      setInited(true);

      // Update the autosave hook's last-saved data after initial load
      setTimeout(() => {
        const initialPayload = {
          ...formData,
          alertOnSeverity: d.alertOnSeverity > 0 ? initialSeverity : 0,
        };
        updateLastSavedData(initialPayload);
      }, 0);
    }

    if (fetchConfig.isSuccess && fetchConfig.data && !fetchConfig.data.success) {
      showGenericError();
    }
  }, [
    fetchConfig.isSuccess,
    fetchConfig.data,
    inited,
    form,
    showGenericError,
    updateLastSavedData,
  ]);

  useEffect(() => {
    if (fetchConfig.isError) {
      const err = fetchConfig.error;
      if (!axios.isAxiosError(err) || err.response?.status !== 404) {
        showGenericError();
      }
    }
  }, [fetchConfig.isError, fetchConfig.error, showGenericError]);

  /* ------------------------------------------------------------------------
   * Render guards
   * ---------------------------------------------------------------------- */
  if (fetchConfig.isLoading) return <Loading />;

  const isRealError =
    fetchConfig.isError &&
    (!axios.isAxiosError(fetchConfig.error) ||
      fetchConfig.error.response?.status !== 404);

  const isControlledFailure =
    fetchConfig.isSuccess && fetchConfig.data && !fetchConfig.data.success;

  if (isRealError || isControlledFailure) {
    return (
      <Flex justify="center" className={styles.fetchFailedContainer}>
        <Card w="50%" className={styles.fetchFailed}>
          <Text>{t('page.userSettings.vulnAlerts.validation.fetchFailed')}</Text>
        </Card>
      </Flex>
    );
  }

  /* ------------------------------------------------------------------------
   * UI
   * ---------------------------------------------------------------------- */
  return (
    <div className="anim-slideInDownShort">
      <Flex maw={650} w="100%" mx="auto" direction="column">
        {/* ---------- Header ---------- */}
        <Flex justify="space-between" align="center" mb={20}>
          <Title order={2}>{t('page.userSettings.vulnAlerts.title')}</Title>
        </Flex>
        <Divider mb={32} />

        {/* ---------- Daily alerts toggle ---------- */}
        <Flex justify="space-between" mb={10}>
          <Text fw={700}>{t('page.userSettings.vulnAlerts.dailyAlerts')}</Text>
          <Switch
            aria-label={t('page.userSettings.vulnAlerts.dailyAlerts')}
            checked={form.values.isActive}
            onChange={(e) => {
              const isActive = e.currentTarget.checked;
              markAsUserInteraction();
              form.setFieldValue('isActive', isActive);
              const {
                alertOnKev,
                alertOnRecommendation,
                targetEmails,
                alertOnSeverity: currentSeverity,
              } = form.values;

              const payload = {
                isActive,
                alertOnKev,
                alertOnRecommendation,
                alertOnSeverity: severityEnabled ? currentSeverity : 0,
                targetEmails,
              };
              saveConfig.mutate(payload);
            }}
          />
        </Flex>
        <Text className={styles.description} mb={24}>
          {t('page.userSettings.vulnAlerts.dailyAlertsDescription')}
        </Text>

        {form.values.isActive && (
          <>
            {/* ================================================================
             * CRITERIA (KEV, Recommendation, Severity)
             * ============================================================== */}
            <Card p={24} mb={16}>
              <Flex align="center" gap={4} mb={4}>
                <Icon icon="sliders" className={styles.slidersIcon} />
                <Text fw={700}>
                  {t('page.userSettings.vulnAlerts.criteria')}
                  <span className={styles.requiredStar}>*</span>
                </Text>
                {criteriaError && (
                  <Flex
                    direction="row"
                    align="center"
                    gap={4}
                    className={styles.noCriteriaSelectedErrorContainer}
                  >
                    <Icon classNames={styles.error} icon="triangle-exclamation" />
                    <Text color="red">
                      {t(
                        'page.userSettings.vulnAlerts.validation.selectAtLeastOneOption',
                      )}
                    </Text>
                  </Flex>
                )}
              </Flex>

              <Box ml="xl">
                <Text className={styles.description} mb={24}>
                  {t('page.userSettings.vulnAlerts.criteriaDescription')}
                </Text>

                {/* --- CISA KEV ------------------------------------------------ */}
                <ManifestCheckbox
                  disabled={saveConfig.isLoading}
                  label={t('page.userSettings.vulnAlerts.onKevList')}
                  {...form.getInputProps('alertOnKev', { type: 'checkbox' })}
                  onChange={(e) => {
                    markAsUserInteraction();
                    form.getInputProps('alertOnKev', { type: 'checkbox' }).onChange(e);
                  }}
                  mb={24}
                />

                {/* --- Recommendation toggle & checkboxes -------------------- */}
                <Flex direction="column">
                  <Flex direction="row" gap={10} mb={recEnabled ? 0 : 24}>
                    <ManifestCheckbox
                      disabled={saveConfig.isLoading}
                      label={
                        <Flex direction="row" gap={4}>
                          {recommendationLabel}{' '}
                          {recEnabled && form.errors.alertOnRecommendation && (
                            <Flex direction="row" align="center" gap={4}>
                              <Icon
                                classNames={styles.error}
                                icon="triangle-exclamation"
                              />
                              <Text color="red" size="sm">
                                {form.errors.alertOnRecommendation}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      }
                      checked={recEnabled}
                      onChange={(e) => {
                        markAsUserInteraction();
                        const on = e.currentTarget.checked;
                        setRecEnabled(on);
                        if (!on) {
                          // If recommendations criteria is toggled off, clear selected recommendations
                          form.setFieldValue('alertOnRecommendation', []);
                        }
                      }}
                      mb={recEnabled ? 12 : 0}
                    />
                  </Flex>

                  {recEnabled && (
                    <Flex direction="column" ml="xl" mb={20}>
                      {RECOMMENDATION_KEYS.map((key) => (
                        <ManifestCheckbox
                          disabled={saveConfig.isLoading}
                          key={key}
                          label={t(`page.userSettings.vulnAlerts.recommendation.${key}`)}
                          checked={form.values.alertOnRecommendation.includes(key)}
                          onChange={(e) => {
                            markAsUserInteraction();
                            const checked = e.currentTarget.checked;
                            const current = form.values.alertOnRecommendation;
                            form.setFieldValue(
                              'alertOnRecommendation',
                              checked
                                ? [...current, key]
                                : current.filter((k) => k !== key),
                            );
                          }}
                          mb={12}
                        />
                      ))}
                    </Flex>
                  )}
                </Flex>

                {/* --- Severity threshold ------------------------------------ */}
                <Flex direction="column" mb={24}>
                  <ManifestCheckbox
                    disabled={saveConfig.isLoading}
                    label={t('page.userSettings.vulnAlerts.severityThreshold', {
                      severity: severityEnabled
                        ? `${form.values.alertOnSeverity} ${t('page.userSettings.vulnAlerts.orHigher')}`
                        : '',
                    })}
                    checked={severityEnabled}
                    onChange={(e) => {
                      markAsUserInteraction();
                      const on = e.currentTarget.checked;
                      setSeverityEnabled(on);
                      if (!on) {
                        // Store current severity slider value, then reset to 0 when disabling
                        setPrevSeverity(form.values.alertOnSeverity);
                        form.setFieldValue('alertOnSeverity', 0);
                      } else {
                        // Restore previous severity value when re-enabling
                        form.setFieldValue('alertOnSeverity', prevSeverity);
                      }
                    }}
                    mb={severityEnabled ? 12 : 0}
                  />

                  {severityEnabled && (
                    <Box ml="xl" mb={12}>
                      <Slider
                        value={form.values.alertOnSeverity}
                        onChange={(val) => form.setFieldValue('alertOnSeverity', val)}
                        onChangeEnd={() => {
                          markAsUserInteraction();
                          saveConfig.mutate(createSavePayload());
                        }}
                        min={0.1}
                        max={10}
                        step={0.1}
                        ml="xl"
                        inverted
                      />
                    </Box>
                  )}
                </Flex>
              </Box>
            </Card>

            {/* ================================================================
             * DELIVERY METHOD (Email)
             * ============================================================== */}
            <Card p={24}>
              <Flex align="center" gap={4} mb={4}>
                <Icon icon="paper-plane" className={styles.slidersIcon} />
                <Text fw={700}>{t('page.userSettings.vulnAlerts.deliveryMethod')}</Text>
              </Flex>

              <Box ml="xl">
                <MultiSelect
                  disabled={saveConfig.isLoading}
                  withinPortal
                  data={emailOptions}
                  value={form.values.targetEmails}
                  onChange={(vals) => {
                    markAsUserInteraction();
                    form.setFieldValue('targetEmails', vals);
                  }}
                  label={
                    <>
                      {t('page.userSettings.vulnAlerts.sendEmailTo')}
                      <span className={styles.requiredStar}>*</span>
                    </>
                  }
                  searchable
                  creatable
                  searchValue={emailSearch}
                  onSearchChange={setEmailSearch}
                  getCreateLabel={(query) => `+ ${t('global.add')} ${query}`}
                  onCreate={handleCreateEmail}
                  placeholder={t('page.userSettings.vulnAlerts.emailPlaceholder')}
                  error={form.errors.targetEmails}
                  rightSection={<span />} // hide default dropdown icon
                  rightSectionWidth={0}
                  classNames={{
                    label: styles.deliveryMethodLabel,
                    value: styles.deliveryMethodValue,
                  }}
                  /* Intercept 'Enter' key to add the email instead of submitting form */
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (emailSearch.trim()) {
                        handleCreateEmail(emailSearch);
                        setEmailSearch('');
                      }
                    }
                  }}
                />
              </Box>
            </Card>
          </>
        )}
      </Flex>
    </div>
  );
};
